import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useNotification } from 'hooks/ui/useNotification';
import { api } from 'lib/httpClient';
import { Api } from 'further-types/organisation-user';

async function listOrgUsers({ queryKey }) {
  const [, organisationId] = queryKey;

  const {
    data: { data },
  } = await api.get(`organisation/${organisationId}/user/list`);
  return data;
}
interface Params {
  organisationId: string;
}

export function useListOrganisationUsers({ organisationId }: Params) {
  return useQuery<Api.ListOrganisationUsersResponse>({
    queryKey: ['organisation-users', organisationId],
    queryFn: listOrgUsers,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!organisationId,
  });
}

export function useDeleteOrganisationUser({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();

  return useMutation({
    mutationFn: (orgUserId: string) => {
      return api.delete(`organisation/user/${orgUserId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Organisation user deleted successfully.');
    },
    onError: () => {
      message.error('Error deleting organisation user.');
    },
  });
}

export function useCreateOrganisationUser({ organisationId }: Params) {
  const queryClient = useQueryClient();
  const message = useNotification();

  return useMutation({
    mutationFn: (data: Api.CreateOrganisationUserParams) => {
      return api.post(`organisation/user`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organisation-users', organisationId],
      });
      message.success('Organisation user created successfully.');
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.responseMsg ||
          'Error creating organisation user.',
      );
    },
  });
}

export function useOrganisationUser(params: Params) {
  return {
    create: useCreateOrganisationUser(params),
    remove: useDeleteOrganisationUser(params),
  };
}
